import { useMemo, useRef, useState } from 'react';

import { usePendingRangeHighlight } from '../../../hooks/usePendingRangeHighlight';
import { useSelectionStore } from '../../../stores/selection-store';
import { anchorLeft, SelectionToolbar } from '../../SelectionToolbar';

import { alignHunk, collapseWhitespace, type DiffHunk, type SplitCell, type SplitCellKind } from './parseDiff';

export type DiffViewMode = 'split' | 'unified';

const LINE_STYLE: Record<SplitCellKind, string> = {
  add: 'bg-diff-add-bg text-diff-add',
  del: 'bg-diff-del-bg text-diff-del',
  context: 'text-fg-secondary',
  empty: 'bg-surface-2/40',
};

const SIGN: Record<SplitCellKind, string> = { add: '+', del: '-', context: ' ', empty: ' ' };

function LineNumber({ value }: { value: number | null }) {
  return <span className="w-10 shrink-0 select-none pr-2 text-right tabular-nums text-fg-tertiary">{value ?? ''}</span>;
}

function UnifiedView({ hunks }: { hunks: DiffHunk[] }) {
  return (
    <>
      {hunks.map((hunk, hunkIndex) => (
        <div key={hunkIndex}>
          <div className="bg-surface-2/60 px-3 py-1 text-fg-tertiary">{hunk.header}</div>
          {hunk.lines.map((line, lineIndex) => (
            <div
              key={lineIndex}
              data-line={line.newNumber ?? line.oldNumber ?? undefined}
              className={`flex whitespace-pre ${LINE_STYLE[line.kind]}`}
            >
              <LineNumber value={line.oldNumber} />
              <LineNumber value={line.newNumber} />
              <span className="shrink-0 select-none pr-1 pl-2">{SIGN[line.kind]}</span>
              <span>{line.content}</span>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/** One side of a split row: line number, sign, content — styled by cell kind. */
function SplitSide({ cell }: { cell: SplitCell }) {
  return (
    <div data-line={cell.number ?? undefined} className={`flex min-w-0 flex-1 whitespace-pre ${LINE_STYLE[cell.kind]}`}>
      <LineNumber value={cell.number} />
      <span className="shrink-0 select-none pr-1 pl-2">{SIGN[cell.kind]}</span>
      <span className="min-w-0">{cell.content}</span>
    </div>
  );
}

function SplitView({ hunks }: { hunks: DiffHunk[] }) {
  return (
    <>
      {hunks.map((hunk, hunkIndex) => (
        <div key={hunkIndex}>
          <div className="bg-surface-2/60 px-3 py-1 text-fg-tertiary">{hunk.header}</div>
          {alignHunk(hunk).map((row, rowIndex) => (
            <div key={rowIndex} className="flex">
              <SplitSide cell={row.left} />
              <div className="w-px shrink-0 bg-line-subtle" aria-hidden />
              <SplitSide cell={row.right} />
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

interface SelectionPopup {
  x: number;
  y: number;
  startLine: number;
  endLine: number;
  snippet: string;
}

/** Walks up from a selection endpoint to the nearest row carrying a line number. */
function nearestLine(node: Node | null): number | null {
  let el = node instanceof Element ? node : node?.parentElement;
  while (el) {
    const raw = el.getAttribute?.('data-line');
    if (raw) return Number(raw);
    el = el.parentElement;
  }
  return null;
}

export function DiffView({
  path,
  hunks,
  view = 'unified',
  hideWhitespace = false,
}: {
  path: string;
  hunks: DiffHunk[];
  view?: DiffViewMode;
  hideWhitespace?: boolean;
}) {
  const shown = useMemo(() => (hideWhitespace ? collapseWhitespace(hunks) : hunks), [hunks, hideWhitespace]);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popup, setPopup] = useState<SelectionPopup | null>(null);
  const addCode = useSelectionStore((s) => s.addCode);

  /** The span the open popover is about, kept so it can stay painted once the note field takes
   * focus and collapses the real selection. */
  const pendingRange = useRef<Range | null>(null);
  // Its own `::highlight()` name: the thread's popover can be open in the same view.
  usePendingRangeHighlight(pendingRange, popup, 'diff-pending');

  const onMouseUp = (e: React.MouseEvent) => {
    // A click inside our own floating panel also bubbles a mouseup here — ignore it so the
    // panel doesn't get yanked out from under the click that's about to fire on it.
    if (popoverRef.current?.contains(e.target as Node)) return;

    const selection = window.getSelection();
    const container = containerRef.current;
    if (!selection || selection.isCollapsed || selection.rangeCount === 0 || !container) {
      setPopup(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const text = selection.toString();
    if (!container.contains(range.commonAncestorContainer) || !text.trim()) {
      setPopup(null);
      return;
    }

    const start = nearestLine(range.startContainer);
    const end = nearestLine(range.endContainer);
    if (start === null && end === null) {
      setPopup(null);
      return;
    }

    // Cloned: the live range collapses along with the selection the moment focus moves.
    pendingRange.current = range.cloneRange();

    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    setPopup({
      x: anchorLeft(rect.left - containerRect.left, container.clientWidth),
      y: rect.bottom - containerRect.top,
      startLine: Math.min(start ?? end!, end ?? start!),
      endLine: Math.max(start ?? end!, end ?? start!),
      snippet: text,
    });
  };

  const close = () => {
    pendingRange.current = null;
    setPopup(null);
  };

  if (hunks.length === 0) {
    return <div className="px-3 py-2 text-xs text-fg-tertiary">No textual diff to show.</div>;
  }

  return (
    <div
      ref={containerRef}
      onMouseUp={onMouseUp}
      className="quotable-selection relative overflow-x-auto bg-code-bg font-mono text-2xs leading-5"
    >
      {view === 'split' ? <SplitView hunks={shown} /> : <UnifiedView hunks={shown} />}
      {popup && (
        <SelectionToolbar
          ref={popoverRef}
          style={{ left: popup.x, top: popup.y + 6 }}
          onCancel={close}
          onAdd={(annotate) => {
            addCode({ path, startLine: popup.startLine, endLine: popup.endLine, snippet: popup.snippet }, annotate);
            window.getSelection()?.removeAllRanges();
            close();
          }}
        />
      )}
    </div>
  );
}
